<template>
  <section
    class="compare dark"
    id="comparison"
    :class="{ 'has-label-column': labelColumn }"
  >
    <div class="container">
      <section-title
        align="left"
        tone="dark"
        data-aos="fade-up"
        :kicker="kicker"
        :title="title"
        :lede="lede"
      />

      <!-- One panel per option. A bar drawn to scale against the year on a
           plan, so the length carries the argument before the figures are
           read. -->
      <div class="save-grid">
        <article v-for="panel in panels" :key="panel.name" class="save-panel" data-aos="fade-up">
          <div class="save-head">
            <h3>{{ panel.name }}</h3>
            <p v-if="panel.save" class="save-chip">
              {{ t('Saves') }}
              <b>{{ panel.save }}</b>
            </p>
          </div>

          <div
            v-for="bar in panel.bars"
            :key="bar.label"
            class="bar-row"
            :class="bar.flat ? 'is-flat' : 'is-pack'"
          >
            <span class="bar-label">{{ bar.label }}</span>
            <span class="bar-track">
              <span class="bar-fill" :style="{ width: bar.share + '%' }"></span>
            </span>
            <span class="bar-value">
              <s v-if="bar.flat">{{ bar.value }}</s>
              <template v-else>{{ bar.value }}</template>
            </span>
          </div>

          <p class="save-note">{{ panel.note }}</p>
        </article>
      </div>

      <!-- The three offer facts, closing the band as a ruled strip. -->
      <ul class="promo-stats">
        <li v-for="stat in stats" :key="stat.label" data-aos="fade-up">
          <span class="promo-stat-value">{{ stat.value }}</span>
          <span class="promo-stat-label">{{ stat.label }}</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* ---- The comparison, on its own band ------------------------------------
   Split off the offer band, so the poster keeps the heading company and the
   figures get a panel of their own. Opens with its own section title, then
   one ruled panel per option, then the offer facts as a closing strip.

   The ground is the promo ink, the same fill the offer band above uses. The
   two used to meet as two different darks, ink above the global section.dark
   grey, which read as a mis-rendered band rather than as a seam. The hairline
   and the doubled gap keep the boundary legible without a second colour. */
.compare {
  background: $ink;
  border-top: 1px solid $ink-line;

  /* The offer band above carries the other half of the gap, so the seam sits
     in the middle of it, reading as a divider rather than as one long void. */
  padding-top: 4rem;
}

/* ---- One ruled panel per option ---------------------------------------- */
.save-grid {
  display: grid;
  gap: 2.5rem;

  @media (min-width: 992px) {
    gap: 3.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Ruled rather than filled, so the two panels read as two columns of the
   same ledger instead of two cards floating on the band. */
.save-panel {
  border-top: 2px solid $on-dark-line-strong;
  min-width: 0;
  padding-top: 1.5rem;
}

.save-head {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  justify-content: space-between;
  margin-bottom: 1.4rem;

  h3 {
    color: $on-dark-strong;
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    margin: 0;
  }
}

/* The saving, in the offer pink: the number a visitor came for is the one
   that carries colour in the panel. */
.save-chip {
  color: $on-dark-body;
  font-size: 0.82rem;
  margin: 0;

  b {
    color: $secondary;
    font-family: $headings-font-family;
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
}

.save-note {
  color: $on-dark-muted;
  font-size: 0.85rem;
  line-height: 1.6;
  margin: 1.1rem 0 0;
}

/* ---- The bars, drawn to scale -------------------------------------------
   One bar per figure, all to the same scale, so length is the argument and
   the saving lands before the figures are read. A panel may carry one bar or
   a pair; the gap between a pair is the row's own, not each panel's. */
.bar-row {
  align-items: center;
  display: grid;
  gap: 0.35rem 0.9rem;
  grid-template-columns: minmax(0, 1fr) auto;

  & + & {
    margin-top: 0.7rem;
  }
}

/* The label sits on its own line by default: these are phrases, not the
   two-word axes the annual pack page plots against, and a phrase in a fixed
   column pushes the bar off the panel. */
.bar-label {
  color: $on-dark-muted;
  font-size: 0.82rem;
  grid-column: 1 / -1;
}

/* Two-word axes get a column of their own instead, so the pair aligns down
   the panel and the bars share one starting edge. */
.compare.has-label-column {
  @media (min-width: 576px) {
    .bar-row {
      grid-template-columns: 9rem minmax(0, 1fr) auto;
    }

    .bar-label {
      grid-column: auto;
    }
  }
}

.bar-track {
  background: $ink-fill;
  border-radius: $r-pill;
  display: block;
  height: 0.5rem;
  overflow: hidden;
  width: 100%;
}

.bar-fill {
  background: linear-gradient(90deg, $primary, $ink-accent);
  border-radius: inherit;
  display: block;
  height: 100%;
}

.bar-value {
  font-family: $headings-font-family;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

/* The year on a list rate: muted bar, struck-through figure. */
.bar-row.is-flat {
  .bar-track {
    background: $on-dark-fill;
  }

  .bar-fill {
    background: $on-dark-line-strong;
  }

  .bar-value {
    color: $on-dark-muted;

    s {
      text-decoration-color: $on-dark-line-strong;
      text-decoration-thickness: 2px;
    }
  }
}

/* The offer: the lit bar, and the figure that matters. */
.bar-row.is-pack .bar-value {
  color: $on-dark-strong;
}

/* ---- Offer facts --------------------------------------------------------
   The three numbers that decide the offer, closing the comparison band as a
   ruled strip across the full column. Stacked on phones, three across from
   768px. */
.promo-stats {
  border-top: 1px solid $ink-line;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr);
  margin: 3.5rem 0 0;
  padding-top: 1.5rem;

  @media (min-width: 768px) {
    gap: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  li {
    padding: 0;

    /* Only split the row once the three are side by side, so the rule is a
       divider between columns rather than a stray line above stacked text. */
    @media (min-width: 768px) {
      border-left: 1px solid $ink-line;
      padding: 0 1.5rem;

      &:first-child {
        border-left: 0;
        padding-left: 0;
      }
    }
  }
}

.promo-stat-value {
  color: $on-dark-strong;
  display: block;
  font-family: $headings-font-family;
  font-size: clamp(1.3rem, 2.6vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.promo-stat-label {
  color: $ink-body;
  display: block;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  margin-top: 0.35rem;
  text-transform: uppercase;
}
</style>

<script>
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    SectionTitle
  },

  props: {
    kicker: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      default: ''
    },
    /* One entry per option being compared:
       { name, save, note, bars: [{ label, value, share, flat }] }
       `save` is blank for an option with nothing to claim, `flat` marks the
       list-rate bar (muted fill, struck-through figure), `share` is the bar
       length as a percentage of the scale. */
    panels: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    },
    /* Whether the bar labels get a column of their own. False keeps each
       label on its own line above its bar, which is what a phrase-length
       label needs; true is for two-word axes. */
    labelColumn: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    /* Every word this panel prints is shared chrome now, so it reads them from
       the root composer instead of carrying three empty dictionaries. */
    const { t } = useI18n();

    return { t };
  }
};
</script>
